/** Каркас страницы: боковое меню, шапка, переключатель темы. */

import { useEffect, useState, type ReactNode } from 'react';
import { ACCESS_LEVEL_LABEL, hasCapability, type Capability, type SessionUser } from '@ak/shared';

import { liveUpdatesEnabled, useLiveConnection } from '../lib/live.ts';
import { useLinkHandler } from '../lib/router.ts';
import { apiLogout } from '../lib/api.ts';
import type { SelectedGuild } from '../lib/guilds.ts';
import { GuildSwitcher, LogoutButton } from './guild-switcher.tsx';
import {
  IconApplications,
  IconAudit,
  IconBroadcast,
  IconEvents,
  IconLock,
  IconLogout,
  IconMenu,
  IconMoon,
  IconOverview,
  IconPlayers,
  IconRollbacks,
  IconSettings,
  IconStats,
  IconSun,
  IconTactics,
} from './icons.tsx';
import { Button } from './ui.tsx';

interface NavItem {
  href: string;
  label: string;
  icon: ReactNode;
  capability: Capability;
  /** Раздел меню: с заголовками список из восьми пунктов читается легче. */
  group: 'Сообщество' | 'Управление';
}

const NAV_ITEMS: NavItem[] = [
  { href: '/', label: 'Обзор', icon: <IconOverview />, capability: 'events.view', group: 'Сообщество' },
  { href: '/events', label: 'Наборы', icon: <IconEvents />, capability: 'events.view', group: 'Сообщество' },
  { href: '/players', label: 'Игроки', icon: <IconPlayers />, capability: 'players.view', group: 'Сообщество' },
  {
    href: '/applications',
    label: 'Заявки',
    icon: <IconApplications />,
    capability: 'applications.review',
    group: 'Сообщество',
  },
  { href: '/stats', label: 'Статистика', icon: <IconStats />, capability: 'stats.view', group: 'Сообщество' },
  { href: '/rollbacks', label: 'Откаты', icon: <IconRollbacks />, capability: 'rollbacks.view', group: 'Управление' },
  { href: '/tactics', label: 'Дефолты', icon: <IconTactics />, capability: 'tactics.manage', group: 'Управление' },
  { href: '/broadcast', label: 'Рассылка', icon: <IconBroadcast />, capability: 'events.manage', group: 'Управление' },
  { href: '/audit', label: 'Журнал', icon: <IconAudit />, capability: 'audit.view', group: 'Управление' },
  { href: '/settings', label: 'Настройки', icon: <IconSettings />, capability: 'settings.edit', group: 'Управление' },
];

const NAV_GROUPS: NavItem['group'][] = ['Сообщество', 'Управление'];

function useTheme(): [string, () => void] {
  // Разметки может не быть вовсе: этим же кодом меню отрисовывается в дымовых
  // тестах, где никакого браузера вокруг нет.
  const [theme, setTheme] = useState<string>(() =>
    typeof document !== 'undefined' && document.documentElement.classList.contains('dark') ? 'dark' : 'light',
  );

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('ak-theme', theme);
  }, [theme]);

  return [
    theme,
    () => {
      setTheme((current) => (current === 'dark' ? 'light' : 'dark'));
    },
  ];
}

/**
 * Состояние живых обновлений.
 *
 * Точка вместо надписи: пока всё в порядке, сообщать не о чем — и только
 * при обрыве появляется текст, который стоит прочитать.
 */
function LiveIndicator({ connected }: { connected: boolean }): ReactNode {
  return (
    <span
      className="inline-flex items-center gap-2 rounded-full px-2.5 py-1 text-xs font-medium"
      style={{
        background: connected ? 'var(--positive-soft)' : 'var(--warning-soft)',
        color: connected ? 'var(--positive)' : 'var(--warning)',
      }}
      title={connected ? 'Данные обновляются в реальном времени' : 'Соединение потеряно, идёт переподключение'}
    >
      <span
        className={`size-1.5 rounded-full ${connected ? 'animate-pulse' : ''}`}
        style={{ background: 'currentColor' }}
      />
      <span className={connected ? 'hidden lg:inline' : ''}>{connected ? 'В эфире' : 'Переподключение…'}</span>
    </span>
  );
}

export function Layout({
  user,
  guild,
  inviteUrl,
  // Аватар бота больше не показывается: место занимает знак CARTEL.
  botAvatarUrl: _botAvatarUrl,
  currentPath,
  children,
}: {
  user: SessionUser;
  guild: SelectedGuild;
  inviteUrl: string | null;
  botAvatarUrl: string | null;
  currentPath: string;
  children: ReactNode;
}): ReactNode {
  const [theme, toggleTheme] = useTheme();
  const onLinkClick = useLinkHandler();
  const connected = useLiveConnection();
  const [menuOpen, setMenuOpen] = useState(false);

  // Показываем все разделы, включая закрытые: спрятанный пункт выглядит
  // как пропажа возможности, а видимый с замком — как граница прав.
  const items = NAV_ITEMS.map((item) => ({ ...item, allowed: hasCapability(user.level, item.capability) }));

  const logout = (): void => {
    void apiLogout();
  };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[268px_1fr]">
      {/* Затемнение под выдвинутым меню: закрывает его по нажатию мимо и
          отделяет от содержимого, которое иначе просвечивает сквозь. */}
      {menuOpen && (
        <button
          type="button"
          aria-label="Закрыть меню"
          className="fixed inset-0 z-30 lg:hidden"
          style={{ background: 'rgba(0,0,0,0.5)' }}
          onClick={() => {
            setMenuOpen(false);
          }}
        />
      )}

      {/*
        На узком экране меню выезжает слева поверх содержимого, а не
        раскрывается сверху: раньше оно вставало в поток и отжимало страницу
        вниз — приходилось прокручивать обратно, чтобы увидеть, куда попал.
        На широком остаётся обычной колонкой.
      */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-72 max-w-[85vw] overflow-y-auto border-r transition-transform duration-200 ease-out ${
          menuOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:sticky lg:top-0 lg:z-auto lg:h-screen lg:w-auto lg:max-w-none lg:translate-x-0`}
        style={{ background: 'var(--surface-panel)', borderColor: 'var(--border-subtle)' }}
      >
        <div className="flex h-full flex-col p-4">
          <div className="mb-7 flex items-center gap-3 px-1 pt-2">
            <span className="brand-mark" aria-hidden="true" />
            <div className="min-w-0">
              <div className="brand-name truncate text-base">Cartel</div>
              <div className="text-[0.625rem] uppercase tracking-[0.22em]" style={{ color: 'var(--text-muted)' }}>
                панель управления
              </div>
            </div>
          </div>

          <nav className="flex flex-col gap-5">
            {NAV_GROUPS.map((group) => {
              const groupItems = items.filter((item) => item.group === group);
              if (groupItems.length === 0) {
                return null;
              }

              return (
                <div key={group}>
                  <div
                    className="mb-1.5 px-3 text-[0.6875rem] font-semibold tracking-[0.08em] uppercase"
                    style={{ color: 'var(--text-muted)' }}
                  >
                    {group}
                  </div>
                  <div className="flex flex-col gap-0.5">
                    {groupItems.map((item) => {
                      const active = item.href === '/' ? currentPath === '/' : currentPath.startsWith(item.href);
                      const inner = (
                        <>
                          <span className="shrink-0 opacity-90">{item.icon}</span>
                          <span className="flex-1">{item.label}</span>
                          {!item.allowed && (
                            <span style={{ color: 'var(--text-muted)' }} aria-label="закрыт">
                              <IconLock size={14} />
                            </span>
                          )}
                        </>
                      );

                      /*
                       * Закрытый пункт виден, но не нажимается. Раньше он вёл
                       * на страницу «раздел закрыт»; с появлением уровня
                       * «игрок» так ходить стало некуда — рядовому игроку в
                       * управлении делать нечего, и объяснять ему устройство
                       * ролей незачем. Сам пункт остаётся на месте: пропажа
                       * выглядела бы как поломка.
                       */
                      if (!item.allowed) {
                        return (
                          <span
                            key={item.href}
                            className="nav-link"
                            aria-disabled="true"
                            title="Раздел закрыт для вашей роли"
                            style={{ opacity: 0.55, cursor: 'default' }}
                          >
                            {inner}
                          </span>
                        );
                      }

                      return (
                        <a
                          key={item.href}
                          href={item.href}
                          onClick={(event) => {
                            onLinkClick(event);
                            setMenuOpen(false);
                          }}
                          className="nav-link"
                          aria-current={active ? 'page' : undefined}
                        >
                          {inner}
                        </a>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>

          <div className="mt-auto pt-6">
            <div
              className="rounded-2xl px-3 py-2.5"
              style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)' }}
            >
              <div className="flex flex-col items-center gap-2 text-center">
                {user.avatarUrl === null ? (
                  <div
                    className="grid size-10 shrink-0 place-items-center rounded-full font-semibold"
                    style={{ background: 'var(--accent-soft)', color: 'var(--accent)', fontSize: '0.875rem', fontFamily: 'var(--font-rounded)' }}
                  >
                    {user.displayName.slice(0, 1).toUpperCase()}
                  </div>
                ) : (
                  <img src={user.avatarUrl} alt="" className="size-10 shrink-0 rounded-full" />
                )}
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold" style={{ fontFamily: 'var(--font-rounded)' }}>
                    {user.displayName}
                  </div>
                  <div
                    className="text-xs font-bold uppercase tracking-wider"
                    style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-rounded)' }}
                  >
                    {ACCESS_LEVEL_LABEL[user.level]}
                  </div>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="mt-2.5 w-full" onClick={logout} style={{ fontFamily: 'var(--font-rounded)' }}>
                <IconLogout size={14} />
                Выйти
              </Button>
            </div>
          </div>
        </div>
      </aside>

      <div className="flex min-w-0 flex-col">
        <header
          className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b px-4 py-2.5 backdrop-blur-md lg:px-8"
          style={{ background: 'color-mix(in oklab, var(--surface-page) 78%, transparent)', borderColor: 'var(--border-subtle)' }}
        >
          <div className="flex min-w-0 items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => {
                setMenuOpen((value) => !value);
              }}
              aria-label="Меню"
            >
              <IconMenu size={18} />
            </Button>

            <GuildSwitcher guild={guild} inviteUrl={inviteUrl} />
          </div>

          <div className="flex items-center gap-2">
            {liveUpdatesEnabled() && <LiveIndicator connected={connected} />}

            <Button
              variant="ghost"
              size="sm"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? 'Включить светлую тему' : 'Включить тёмную тему'}
            >
              {theme === 'dark' ? <IconSun size={18} /> : <IconMoon size={18} />}
            </Button>
          </div>
        </header>

        <main className="animate-in min-w-0 flex-1 px-4 py-6 lg:px-8 lg:py-8">{children}</main>
      </div>
    </div>
  );
}

/**
 * Каркас без бокового меню.
 *
 * Пока сервер не размечен, разделы админки показывать нечего: каналов и
 * ролей ещё нет. Остаются только переключатель сервера и выход.
 */
export function PlainShell({
  guild,
  inviteUrl,
  children,
}: {
  guild: SelectedGuild;
  inviteUrl: string | null;
  children: ReactNode;
}): ReactNode {
  return (
    <div className="min-h-screen">
      <header
        className="flex items-center justify-between gap-3 border-b px-4 py-3 lg:px-8"
        style={{ borderColor: 'var(--border-subtle)' }}
      >
        <GuildSwitcher guild={guild} inviteUrl={inviteUrl} />
        <LogoutButton />
      </header>
      <main className="animate-in px-4 py-10 lg:px-8">{children}</main>
    </div>
  );
}
